import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtClock } from '../lib/format.js';
import { CorrectionRequestModal } from './CorrectionRequestModal.jsx';

// Personal phone clock-in banner (Round 38). Polls /api/me/timeclock/status
// every 30s. Three states:
//   - punched-in:  shows elapsed + Break / End-break / Clock-out buttons
//   - on-break:    shows the running break + End-break button
//   - punched-out: if a shift exists today, prompts Clock in
// Geolocation is requested at clock-in for logging only — there's no
// hard geofence enforcement at v1. Errors fail soft.
export function PunchBanner({ user }) {
  const headers = { 'x-user-id': String(user.id) };
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showCorrection, setShowCorrection] = useState(false);
  // CJ feedback 2026-06-16: staffers should pick a location to clock
  // in to (default = today's scheduled shift's location). Lets them
  // clock in across multiple stores in a single day. Authorized
  // locations only — view_all_locations bypasses the filter.
  const [authorizedLocs, setAuthorizedLocs] = useState([]);
  const [pickedLocId, setPickedLocId] = useState(null);

  async function load() {
    try {
      const r = await api('GET', '/api/me/timeclock/status', null, headers);
      setS(r);
    } catch (e) { /* fail soft — banner just won't show */ }
  }
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  useEffect(() => {
    api('GET', '/api/locations').then((all) => {
      if (!Array.isArray(all)) return setAuthorizedLocs([]);
      if (window.EmpireRoles?.hasCapability?.(user, 'view_all_locations')) {
        return setAuthorizedLocs(all);
      }
      let ids = [];
      if (user.location_ids) { try { ids = JSON.parse(user.location_ids); } catch {} }
      if ((!ids || ids.length === 0) && user.location_id) ids = [user.location_id];
      setAuthorizedLocs(ids.length ? all.filter((l) => ids.includes(l.id)) : all);
    }).catch(() => setAuthorizedLocs([]));
  }, [user.id]);

  // Default the picker to the scheduled shift's location whenever a
  // new status payload lands, BUT don't stomp the staffer's manual
  // choice between polls.
  useEffect(() => {
    if (!s) return;
    if (pickedLocId) return;
    const def = s.next_shift_today?.location_id || s.default_location_id || null;
    if (def) setPickedLocId(def);
  }, [s, pickedLocId]);

  async function withGeo() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve({}),
        { timeout: 4000, maximumAge: 60000 }
      );
    });
  }

  async function punchIn() {
    // R44: no confirm dialog for unscheduled clock-in. The banner
    // already says "No shift scheduled today — clock in unscheduled"
    // so the staffer knows they're off-schedule before tapping.
    setBusy(true); setErr('');
    try {
      const geo = await withGeo();
      const locId = pickedLocId
        || s?.next_shift_today?.location_id
        || s?.default_location_id
        || null;
      if (!locId) { setErr('No location available — contact your manager.'); return; }
      // Only attach the scheduled shift_id if the picked location
      // matches it — otherwise the staffer is intentionally clocking
      // in at a different store and the punch should be standalone.
      const shiftId = (s?.next_shift_today?.location_id === locId)
        ? (s?.next_shift_today?.id || null)
        : null;
      await api('POST', '/api/me/timeclock/punch-in',
        { location_id: locId, shift_id: shiftId, ...geo }, headers);
      load();
    } catch (e) { let m = e.message; try { m = JSON.parse(e.message).error; } catch {} setErr(m); }
    finally { setBusy(false); }
  }
  async function punchOut() {
    if (!confirm('Clock out now?')) return;
    setBusy(true); setErr('');
    try {
      const geo = await withGeo();
      await api('POST', '/api/me/timeclock/punch-out', geo, headers);
      load();
    } catch (e) { let m = e.message; try { m = JSON.parse(e.message).error; } catch {} setErr(m); }
    finally { setBusy(false); }
  }
  async function startBreak(kind) {
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/me/timeclock/break/start', { kind }, headers);
      load();
    } catch (e) { let m = e.message; try { m = JSON.parse(e.message).error; } catch {} setErr(m); }
    finally { setBusy(false); }
  }
  async function endBreak() {
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/me/timeclock/break/end', {}, headers);
      load();
    } catch (e) { let m = e.message; try { m = JSON.parse(e.message).error; } catch {} setErr(m); }
    finally { setBusy(false); }
  }

  if (!s) return null;
  const open = s.open_punch;
  const onBreak = s.on_break;

  // Punched in
  if (open) {
    const elapsed = Math.max(0, Math.round((Date.now() - new Date(open.clock_in_at).getTime()) / 60000));
    const eh = Math.floor(elapsed / 60), em = elapsed % 60;
    return (
      <div className="card" style={{ borderLeft: `4px solid ${onBreak ? '#f59e0b' : '#16a34a'}`, padding: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: onBreak ? '#92400e' : '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {onBreak ? 'On break' : 'Clocked in'}
            </div>
            <div style={{ fontSize: 13, color: '#212121' }}>
              {open.location_name || ''} · {eh}h {em}m so far
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {onBreak ? (
              <button onClick={endBreak} disabled={busy} className="btn-primary">End break</button>
            ) : (
              <>
                <button onClick={() => startBreak('paid_10')} disabled={busy} className="btn-secondary" style={{ fontSize: 12 }}>☕ Paid 10</button>
                <button onClick={() => startBreak('unpaid_30')} disabled={busy} className="btn-secondary" style={{ fontSize: 12 }}>🍽 Unpaid 30</button>
                <button onClick={punchOut} disabled={busy}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Clock out
                </button>
              </>
            )}
          </div>
        </div>
        {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
        <div style={{ marginTop: 6, fontSize: 11, color: '#757575' }}>
          Times wrong?
          {' '}
          <button onClick={() => setShowCorrection(true)}
            style={{ color: 'var(--forest)', background: 'transparent', border: 'none',
                      padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: 11, fontFamily: 'inherit' }}>
            Request a correction
          </button>
        </div>
        {showCorrection && (
          <CorrectionRequestModal user={user}
            defaults={{ location_id: open.location_id || null, punch_id: open.id, request_type: 'edit_punch',
                          shift_date: new Date(open.clock_in_at).toISOString().slice(0,10) }}
            onClose={() => setShowCorrection(false)}
            onSubmitted={() => alert('Correction sent to your manager.')} />
        )}
      </div>
    );
  }

  // Punched out — show the banner whether or not there's a scheduled
  // shift today, so admins (CJ) and anyone with off-schedule punches
  // can clock in without going through the kiosk.
  const sh = s.next_shift_today;
  return (
    <div className="card" style={{ borderLeft: '4px solid var(--gold, #c8a25b)', padding: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#a16207', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {sh ? 'Ready to clock in' : 'Clock in'}
          </div>
          <div style={{ fontSize: 13, color: '#212121' }}>
            {sh
              ? `${fmtClock(sh.start_time)}–${fmtClock(sh.end_time)}${sh.location_name ? ` · ${sh.location_name}` : ''}`
              : 'No shift scheduled today — clock in unscheduled.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {authorizedLocs.length > 1 && (
            <select
              value={pickedLocId || ''}
              onChange={(e) => setPickedLocId(Number(e.target.value) || null)}
              disabled={busy}
              style={{ fontSize: 13, padding: '6px 8px', border: '1px solid #d4d4d4', borderRadius: 6, background: '#fff', fontFamily: 'inherit' }}
              aria-label="Location to clock in to"
            >
              {authorizedLocs.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}
          <button onClick={punchIn} disabled={busy}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {busy ? 'Working…' : 'Clock in'}
          </button>
        </div>
      </div>
      {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      <div style={{ marginTop: 8, fontSize: 11, color: '#757575' }}>
        Forgot to clock in or out?
        {' '}
        <button onClick={() => setShowCorrection(true)}
          style={{ color: 'var(--forest)', background: 'transparent', border: 'none',
                    padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: 11, fontFamily: 'inherit' }}>
          Submit a correction
        </button>
      </div>
      {showCorrection && (
        <CorrectionRequestModal user={user}
          defaults={{ location_id: s?.default_location_id || null }}
          onClose={() => setShowCorrection(false)}
          onSubmitted={() => alert('Correction sent to your manager.')} />
      )}
    </div>
  );
}
